import { Download, Search } from "lucide-react";
import { useMemo, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { Select } from "@/shared/ui/select";

import type { ReportFilters as Filters } from "../api/query-keys";
import { monthRange, recentMonths, type MonthRange } from "../lib/report-month";

import { SecondaryButton } from "./buttons";

interface ReportFiltersProps {
  filters: Filters;
  /** Distinct shop names from the loaded rows — the BE has no shop-list endpoint. */
  shops: string[];
  /** Set a single filter; `undefined` clears it (page deletes the key). */
  onChange: <K extends keyof Filters>(key: K, value: Filters[K] | undefined) => void;
  /**
   * Set the report date range for a chosen month, or clear it ("All time").
   * Kept separate from `onChange` because a month sets two keys at once.
   */
  onMonthChange: (range: MonthRange | null) => void;
  /** Export the currently-visible rows as CSV (client-side). */
  onExport: () => void;
  /** Disables export when there is nothing to export. */
  exportDisabled: boolean;
}

// Marketplace names are brand proper nouns (matches MarketplaceChip's fixed labels).
const MARKETPLACES: { value: "shopee" | "lazada" | "tiktok"; label: string }[] = [
  { value: "shopee", label: "Shopee" },
  { value: "lazada", label: "Lazada" },
  { value: "tiktok", label: "TikTok" },
];

export function ReportFilters({
  filters,
  shops,
  onChange,
  onMonthChange,
  onExport,
  exportDisabled,
}: ReportFiltersProps): JSX.Element {
  const { t } = useTranslation("promotion");

  // Recent months computed once per mount; the selected value is derived from
  // the active `date_from` so the picker stays in sync with the filter state.
  // Parse back to a Date and read the LOCAL month so the value matches the
  // option the user picked (monthRange builds the range from local time).
  const months = useMemo(() => recentMonths(), []);
  const selectedMonth = filters.date_from
    ? (() => {
        const d = new Date(filters.date_from);
        return Number.isNaN(d.getTime())
          ? ""
          : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      })()
    : "";

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2.5" data-testid="report-filters">
      <div className="relative min-w-[200px] flex-1">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-subtle)]"
        />
        <input
          type="search"
          data-testid="report-search"
          placeholder={t("report.search_placeholder")}
          value={filters.search ?? ""}
          onChange={(e) => onChange("search", e.target.value || undefined)}
          className="focus-ring h-9 w-full rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white pl-10 pr-3 text-sm text-[var(--text-primary)] shadow-[var(--sh-xs)] outline-none transition placeholder:text-[var(--text-subtle)] focus:border-[var(--brand-500)]"
        />
      </div>

      <Select
        data-testid="report-filter-month"
        aria-label={t("report.month_label")}
        value={selectedMonth}
        onChange={(e) => onMonthChange(e.target.value ? monthRange(e.target.value) : null)}
      >
        <option value="">{t("report.all_time")}</option>
        {months.map((m) => (
          <option key={m.value} value={m.value}>
            {m.label}
          </option>
        ))}
      </Select>

      <Select
        data-testid="report-filter-marketplace"
        aria-label={t("report.column.marketplace")}
        value={filters.marketplace ?? ""}
        onChange={(e) =>
          onChange("marketplace", (e.target.value || undefined) as Filters["marketplace"])
        }
      >
        <option value="">{t("report.all_platforms")}</option>
        {MARKETPLACES.map((m) => (
          <option key={m.value} value={m.value}>
            {m.label}
          </option>
        ))}
      </Select>

      <Select
        data-testid="report-filter-shop"
        aria-label={t("report.column.shop")}
        value={filters.shop ?? ""}
        onChange={(e) => onChange("shop", e.target.value || undefined)}
      >
        <option value="">{t("report.all_shops")}</option>
        {shops.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </Select>

      <SecondaryButton
        type="button"
        data-testid="report-export-csv"
        onClick={onExport}
        disabled={exportDisabled}
      >
        <Download className="h-4 w-4" aria-hidden />
        {t("report.export_csv")}
      </SecondaryButton>
    </div>
  );
}
